/**
 * Pure calculation helpers for account reconciliation.
 *
 * Business rule:
 * An account is reconciled when its calculated book balance through the
 * statement end date matches the statement closing balance to the cent.
 */

const roundCurrency = (value) => (
  Math.round((Number(value) || 0) * 100) / 100
);

export const getAccountDirection = (account) => (
  account?.type === 'Liability' ? -1 : 1
);

export const calculateReconciliationSummary = (
  entries,
  account,
  statementDate,
  endingBalanceInput
) => {
  if (!account) {
    return {
      startingBalance: 0,
      clearedBalance: 0,
      difference: null,
      isBalanced: false,
      unreconciledIds: [],
      reconciledCount: 0,
      pendingCount: 0
    };
  }

  const direction = getAccountDirection(account);
  const accountEntries = entries.filter((entry) => entry.coa_id === account.id);
  const cutoff = statementDate
    ? new Date(`${statementDate}T23:59:59`).getTime()
    : null;

  const isOnOrBeforeStatementDate = (entry) => {
    if (!cutoff) return true;

    return new Date(`${entry.date}T00:00:00`).getTime() <= cutoff;
  };

  const reconciledEntries = accountEntries.filter((entry) => (
    entry.status === 'Reconciled'
    && isOnOrBeforeStatementDate(entry)
  ));

  const startingBalance = roundCurrency(
    direction * reconciledEntries.reduce(
      (sum, entry) => (
        sum
        + (Number(entry.debit) || 0)
        - (Number(entry.credit) || 0)
      ),
      0
    )
  );

  const pendingEntries = accountEntries.filter((entry) => (
    entry.status !== 'Reconciled'
    && isOnOrBeforeStatementDate(entry)
  ));

  const pendingDelta = roundCurrency(
    direction * pendingEntries.reduce(
      (sum, entry) => (
        sum
        + (Number(entry.debit) || 0)
        - (Number(entry.credit) || 0)
      ),
      0
    )
  );

  const clearedBalance = roundCurrency(startingBalance + pendingDelta);

  const hasEndingBalance = (
    endingBalanceInput !== ''
    && endingBalanceInput !== null
    && endingBalanceInput !== undefined
  );

  const enteredBalance = hasEndingBalance
    ? Number(endingBalanceInput)
    : null;

  const hasValidEndingBalance = (
    enteredBalance !== null
    && Number.isFinite(enteredBalance)
  );

  const difference = hasValidEndingBalance
    ? roundCurrency(enteredBalance - clearedBalance)
    : null;

  const isBalanced = (
    hasValidEndingBalance
    && Math.abs(difference) <= 0.009
  );

  return {
    startingBalance,
    clearedBalance,
    difference,
    isBalanced,
    unreconciledIds: pendingEntries.map((entry) => entry.id),
    reconciledCount: reconciledEntries.length,
    pendingCount: pendingEntries.length
  };
};